import type { Editor } from '@tiptap/core'
import { PluginKey } from '@tiptap/pm/state'
import { npubEncode } from '@nostr/tools/nip19'
import { searchPubKeysFromLocal } from '@/services/client.service'
import { SLASH_COMMAND_BOOL_CHOICES } from '@/lib/slash-command-definition'
import { findArgsMatch, resolveArgsContext } from './argsFlow'
import { createSuggestionRender } from './slash-popup'
import ArgsList, { TArgsListItem } from './ArgsList'

type RendererProps = {
  items: TArgsListItem[]
  command: (item: TArgsListItem) => void
}

const argsSuggestion = {
  char: '\u0000',
  pluginKey: new PluginKey('slashArgsSuggestion'),
  findSuggestionMatch: findArgsMatch,

  // `query` is the partially typed word before the cursor; it filters the list
  items: ({ editor, query }: { editor: Editor; query: string }) => {
    const arg = resolveArgsContext(editor.state.selection.$from)?.arg
    if (!arg) return []

    const q = query.toLowerCase()
    const matches = (s: string) => !q || s.toLowerCase().includes(q)
    const items: TArgsListItem[] = []

    if (arg.type === 'enum' || arg.type === 'bool') {
      const choices = arg.type === 'bool' ? SLASH_COMMAND_BOOL_CHOICES : arg.choices
      for (const choice of choices) {
        if (matches(choice)) items.push({ kind: 'choice', value: choice })
      }
    } else if (arg.type === 'pubkey') {
      for (const pk of searchPubKeysFromLocal(query, 20)) {
        items.push({ kind: 'pubkey', value: npubEncode(pk) })
      }
    } else if (arg.type === 'relay') {
      const relays = (window.hallway.universe?.bigRelayUrls ?? []) as string[]
      for (const url of relays) {
        if (matches(url)) items.push({ kind: 'relay', value: url })
      }
    }
    // other types are free-form: the list only shows the arg description

    return items
  },

  command: ({
    editor,
    range,
    props
  }: {
    editor: Editor
    range: { from: number; to: number }
    props: TArgsListItem
  }) => {
    editor
      .chain()
      .focus()
      .insertContentAt(range, props.value + ' ')
      .run()
  },

  render: createSuggestionRender<RendererProps>(ArgsList)
}

export default argsSuggestion
